import { Plus, Users } from "lucide-react"
import * as React from "react"

import { weakPasswordMessage } from "@/components/domain/password-rules"
import { Button } from "@/components/ui/button"
import { DataTable, Pagination, type Column } from "@/components/ui/data-table"
import { Dialog, DialogTrigger } from "@/components/ui/dialog"
import { EmptyState } from "@/components/ui/empty-state"
import { SectionCard } from "@/components/ui/section-card"
import { ActiveFilterChips } from "@/components/ui/table-filters"
import { type SortOption } from "@/components/ui/table-sort"
import { TableToolbar } from "@/components/ui/table-toolbar"
import { useToast } from "@/components/ui/toast"
import { TABLE_PAGE_SIZES, useTableUrlState, type TableSort } from "@/lib/table"
import { useSlashShortcut } from "@/lib/use-slash-shortcut"

import { useCurrentUser } from "@/features/auth"

import type { Role } from "@/lib/roles"

import { JoinedCell, RoleCell, UserAccountCell, UserActionsCell } from "./users-keys-cells"
import { accountStatus, labelOfRole, type ConsoleUser } from "./users-keys-data"
import { AddUserDialog, RemoveUserDialog } from "./users-keys-dialogs"
import { bannerFor } from "@/lib/api-error-message"
import { ApiError } from "@/lib/errors"

import { useQuery } from "@tanstack/react-query"

import { buildUserFilterSchema, userFiltersToQuery } from "./users-keys-filters"
import {
  useCreateUserMutation,
  useDeleteUserMutation,
  useResendInviteMutation,
  useUpdateUserMutation,
  usersQuery,
} from "./users-keys-api"

const EMPTY_USERS: ConsoleUser[] = []

// All sorted by the backend across the whole list. No "Last seen": the backend records no
// logins, and its `lastSeen` sort is acceptance time, which put active password accounts last.
// Role sorts on the stored value, so A â†’ Z is admin, client, reviewer, sme, viewer.
const USER_SORT_OPTIONS: SortOption[] = [
  { id: "createdAt", label: "Joined", ascLabel: "Oldest first", descLabel: "Newest first" },
  { id: "name", label: "Name", ascLabel: "A â†’ Z", descLabel: "Z â†’ A" },
  { id: "email", label: "Email", ascLabel: "A â†’ Z", descLabel: "Z â†’ A" },
  { id: "role", label: "Role", ascLabel: "A â†’ Z", descLabel: "Z â†’ A" },
]
const USER_SORT_FIELDS: readonly string[] = USER_SORT_OPTIONS.map((o) => o.id)
const DEFAULT_USER_SORT: TableSort = { field: "createdAt", dir: "desc" }

export function UsersKeysPage(): JSX.Element {
  const { toast } = useToast()
  const userSearchRef = React.useRef<HTMLInputElement>(null)
  useSlashShortcut(userSearchRef)

  const [addUserOpen, setAddUserOpen] = React.useState(false)
  const [pendingRemove, setPendingRemove] = React.useState<ConsoleUser | null>(null)

  const userSchema = React.useMemo(() => buildUserFilterSchema(), [])

  // Users list is server-paginated + searchable + sortable via /users.

  const createUserMut = useCreateUserMutation()
  const deleteUserMut = useDeleteUserMutation()
  const resendInviteMut = useResendInviteMutation()
  const updateUserMut = useUpdateUserMutation()
  // Your own row is read-only: an admin cannot deactivate, delete or demote themselves here.
  // Widened to a plain string: auth's UserId and this screen's ConsoleUserId are different
  // brands over the same backend id.
  const meId: string | undefined = useCurrentUser()?.id

  // Users: the query lives in the URL (see lib/table/use-table-url-state.ts) and React Query
  // fetches whatever the URL says. Role and activity go to the backend; nothing is filtered here.
  const usersUrl = useTableUrlState({
    schema: userSchema,
    sortFields: USER_SORT_FIELDS,
    defaultSort: DEFAULT_USER_SORT,
  })
  const usersResult = useQuery(
    usersQuery({
      page: usersUrl.state.page,
      pageSize: usersUrl.state.pageSize,
      search: usersUrl.state.committedSearch,
      sort: usersUrl.state.sort,
      ...userFiltersToQuery(usersUrl.state.filters),
    }),
  )
  const usersTable = {
    state: {
      ...usersUrl.state,
      rows: usersResult.data?.items ?? EMPTY_USERS,
      total: usersResult.data?.total ?? 0,
      isLoading: usersResult.isPending,
      error: usersResult.error,
    },
    // No `refresh`: every mutation invalidates the users query, which refetches on its own.
    actions: usersUrl.actions,
  }

  // A shared or stale link can point past the last page (`?page=9` after people were
  // removed). The backend answers with an empty page; step back to the real last one
  // rather than showing "nothing here" over a list that is not empty. `replace`, because this
  // is a correction and not a page the reader asked for: pushed, it leaves the out-of-range
  // page one Back away, which corrects forward again.
  const servedTotal = usersResult.data?.total
  const servedEmpty = usersResult.data?.items.length === 0
  const { setPage: setUsersPage } = usersUrl.actions
  React.useEffect(() => {
    if (servedTotal === undefined || servedTotal === 0 || !servedEmpty) return
    const lastPage = Math.max(1, Math.ceil(servedTotal / usersUrl.state.pageSize))
    if (usersUrl.state.page > lastPage) setUsersPage(lastPage, { replace: true })
  }, [servedTotal, servedEmpty, usersUrl.state.page, usersUrl.state.pageSize, setUsersPage])

  // Row actions. `mutate` with callbacks rather than an awaited `mutateAsync`: a menu's
  // `onSelect` discards what it returns, so an awaited rejection would be unhandled and the
  // failure invisible â€” these rows have no error surface but the toast.
  const { mutate: resendInvite } = resendInviteMut
  const { mutate: updateUser } = updateUserMut
  const setActive = React.useCallback(
    (u: ConsoleUser, active: boolean) => {
      updateUser(
        { id: u.id, body: { isActive: active } },
        {
          onSuccess: () => {
            toast({
              tone: "success",
              title: active ? `${u.email} reactivated` : `${u.email} deactivated`,
              body: active
                ? "They can sign in again."
                : "They are signed out and cannot sign in until reactivated.",
              action: {
                label: "Undo",
                onAction: () => {
                  updateUser({ id: u.id, body: { isActive: !active } })
                },
              },
            })
          },
          onError: (err) => {
            const banner = bannerFor(err, u.email, "save")
            toast({ tone: "error", title: banner.title, body: banner.body })
          },
        },
      )
    },
    [toast, updateUser],
  )

  // The role select now saves. Undo is a second PATCH back to the previous role.
  const changeRole = React.useCallback(
    (u: ConsoleUser, role: Role) => {
      const previous = u.role
      if (role === previous) return
      updateUser(
        { id: u.id, body: { role } },
        {
          onSuccess: () => {
            toast({
              tone: "success",
              title: `${u.email} is now ${labelOfRole(role)}`,
              action: {
                label: "Undo",
                onAction: () => {
                  updateUser({ id: u.id, body: { role: previous } })
                },
              },
            })
          },
          onError: (err) => {
            const banner = bannerFor(err, `${u.email}'s role`, "save")
            toast({ tone: "error", title: banner.title, body: banner.body })
          },
        },
      )
    },
    [toast, updateUser],
  )

  const userColumns = React.useMemo<Column<ConsoleUser>[]>(
    () => [
      // Account no longer absorbs all the slack (Actions does), so Role sits next to the
      // person it describes instead of drifting to the far side of a wide screen.
      // Every column after Account is centred, header and cell alike.
      {
        id: "account",
        header: "Account",
        width: "minmax(260px,1.2fr)",
        cell: (u) => <UserAccountCell user={u} isSelf={u.id === meId} />,
      },
      {
        id: "role",
        header: "Role",
        width: "280px",
        align: "center",
        cell: (u) => (
          <RoleCell
            user={u}
            // Your own role is read-only: an admin demoting themselves loses this page.
            disabled={u.id === meId || updateUserMut.isPending}
            onChange={(role) => {
              changeRole(u, role)
            }}
          />
        ),
      },
      {
        id: "joined",
        header: "Joined",
        // Wider than Actions: 1.5 shares of the spare width to its 1, and never below 280px.
        width: "minmax(280px,1.5fr)",
        priority: 2,
        align: "center",
        cell: (u) => <JoinedCell iso={u.createdAt} />,
      },
      // Takes the spare width, so Role stays beside the account instead of drifting right.
      // Two icon-and-label actions fit in 260px. Resend is one of them, not its own column.
      {
        id: "actions",
        header: "Actions",
        width: "minmax(260px,1fr)",
        align: "center",
        cell: (u) => (
          <UserActionsCell
            user={u}
            isSelf={u.id === meId}
            busy={
              (updateUserMut.isPending && updateUserMut.variables?.id === u.id) ||
              (resendInviteMut.isPending && resendInviteMut.variables === u.id)
            }
            actions={{
              onResendInvite: () => {
                resendInvite(u.id, {
                  onSuccess: () => {
                    toast({ tone: "success", title: `Invitation resent to ${u.email}` })
                  },
                  onError: (err) => {
                    const banner = bannerFor(err, "the invitation", "save")
                    toast({ tone: "error", title: banner.title, body: banner.body })
                  },
                })
              },
              onSetActive: (active) => {
                setActive(u, active)
              },
              onDelete: () => {
                setPendingRemove(u)
              },
            }}
          />
        ),
      },
    ],
    [
      changeRole,
      meId,
      resendInvite,
      resendInviteMut.isPending,
      resendInviteMut.variables,
      setActive,
      toast,
      updateUserMut.isPending,
      updateUserMut.variables,
    ],
  )

  // The committed search, not the draft: the empty state describes the rows on screen, which
  // were fetched for what is in the URL.
  const usersFiltered =
    usersTable.state.committedSearch.length > 0 || usersTable.state.filterCount > 0

  // A failed list is not a pending one. Same rule the banner above uses: an aborted request
  // (navigation, a newer query) has no title and is not a failure worth reporting either.
  const usersListError = usersTable.state.error
    ? bannerFor(usersTable.state.error, "console users")
    : null
  const usersListFailed = usersListError !== null && usersListError.title !== ""

  return (
    <div className="flex h-full flex-col gap-5 px-6 py-5">
      <LoadErrorBanner error={usersTable.state.error} resource="console users" />

      <SectionCard
        icon={<Users className="size-4" aria-hidden="true" />}
        title="Users"
        subtitle="People who sign in to the console."
        count={usersResult.data?.total}
        countFailed={usersListFailed}
        action={
          <Dialog
            open={addUserOpen}
            onOpenChange={(open) => {
              setAddUserOpen(open)
              // A refusal belongs to the attempt it answered, not to the next time it opens.
              if (!open) createUserMut.reset()
            }}
          >
            <DialogTrigger asChild>
              <Button variant="primary" size="sm" leadingIcon={<Plus />}>
                Add user
              </Button>
            </DialogTrigger>
            <AddUserDialog
              isSubmitting={createUserMut.isPending}
              passwordError={weakPasswordMessage(createUserMut.error)}
              onPasswordEdit={() => {
                if (createUserMut.error) createUserMut.reset()
              }}
              onSubmit={(input) => {
                createUserMut.mutate(
                  {
                    email: input.email,
                    fullName: input.name,
                    role: input.role,
                    mode:
                      input.kind === "invite"
                        ? { kind: "invite" }
                        : { kind: "password", password: input.password },
                  },
                  {
                    onSuccess: () => {
                      setAddUserOpen(false)
                      // Back to page 1, as develop does. The mutation already invalidates
                      // the users list, so this is all it takes for the new row to show.
                      usersTable.actions.setPage(1)
                      toast(
                        input.kind === "invite"
                          ? {
                              tone: "success",
                              title: `Invitation sent to ${input.email}`,
                              body: "They set their own password from the link in the email.",
                            }
                          : {
                              tone: "success",
                              title: `${input.email} added`,
                              body: "They can sign in now. Share their password with them directly.",
                            },
                      )
                    },
                    onError: (err) => {
                      // A weak password is shown on its field in the dialog, which stays open.
                      if (weakPasswordMessage(err)) return
                      // "save", not the default "load": the reader needs to know nothing was
                      // created, not that something failed to load.
                      const banner = bannerFor(
                        err,
                        input.kind === "invite" ? "the invitation" : "the new account",
                        "save",
                      )
                      toast({ tone: "error", title: banner.title, body: banner.body })
                    },
                  },
                )
              }}
            />
          </Dialog>
        }
      >
        <div className="shrink-0 border-t border-border-soft px-5 py-3">
          <TableToolbar
            search={usersTable.state.search}
            onSearchChange={usersTable.actions.setSearch}
            searchRef={userSearchRef}
            searchPlaceholder="Search by name or email"
            searchAriaLabel="Search users"
            searchShortcut="/"
            filterSchema={userSchema}
            filterValues={usersTable.state.filters}
            onFiltersChange={usersTable.actions.setFilters}
            onClearAllFilters={usersTable.actions.clearAllFilters}
            sortOptions={USER_SORT_OPTIONS}
            sort={usersTable.state.sort}
            onSortChange={usersTable.actions.setSort}
          />
          <div className="mt-3">
            <ActiveFilterChips
              schema={userSchema}
              values={usersTable.state.filters}
              onRemove={usersTable.actions.clearFilter}
              onClearAll={usersTable.actions.clearAllFilters}
            />
          </div>
        </div>

        {/* Scrolls both ways. The table never gets narrower than its columns' minimums
            (260 + 280 + 280 + 260, plus gaps and padding â‰ˆ 1160px), so on a small screen
            the wrapper scrolls sideways instead of the table clipping its last columns.
            Holding the width also keeps every column visible: DataTable hides
            `priority: 2` columns (Joined) when it is narrow, and here it never is. */}
        <div className="min-h-0 flex-1 overflow-auto border-t border-border-soft">
          <DataTable<ConsoleUser>
            label="Console users"
            columns={userColumns}
            rows={usersTable.state.rows}
            rowKey={(u) => u.id}
            className="min-w-[1160px] rounded-none border-0"
            stickyHeader
            loading={usersTable.state.isLoading}
            empty={
              <EmptyState
                bare
                reason={usersFiltered ? "filtered" : "empty"}
                title={usersFiltered ? "Nothing matches" : "No users yet"}
                body={
                  usersFiltered
                    ? "Try clearing the search or filters."
                    : "Add someone to give them console access."
                }
                action={
                  usersFiltered ? (
                    <Button
                      size="sm"
                      onClick={() => {
                        usersTable.actions.setSearch("")
                        usersTable.actions.clearAllFilters()
                      }}
                    >
                      Clear filters
                    </Button>
                  ) : null
                }
              />
            }
          />
        </div>

        <div className="shrink-0 border-t border-border-soft">
          <Pagination
            page={usersTable.state.page}
            pageSize={usersTable.state.pageSize}
            total={usersTable.state.total}
            onPageChange={usersTable.actions.setPage}
            onPageSizeChange={usersTable.actions.setPageSize}
            pageSizes={[...TABLE_PAGE_SIZES]}
          />
        </div>
      </SectionCard>

      <Dialog
        open={pendingRemove !== null}
        onOpenChange={(open) => {
          if (!open && !deleteUserMut.isPending) setPendingRemove(null)
        }}
      >
        {pendingRemove && (
          <RemoveUserDialog
            email={pendingRemove.email}
            pendingInvite={accountStatus(pendingRemove) === "pending-invite"}
            isSubmitting={deleteUserMut.isPending}
            onConfirm={() => {
              const removed = pendingRemove
              const wasInvite = accountStatus(removed) === "pending-invite"
              deleteUserMut.mutate(removed.id, {
                onSuccess: () => {
                  setPendingRemove(null)
                  toast({
                    tone: "success",
                    title: wasInvite
                      ? `Invitation to ${removed.email} cancelled`
                      : `${removed.email} deleted`,
                  })
                },
                onError: (err) => {
                  setPendingRemove(null)
                  const { title, body } = deleteFailure(err, removed.email)
                  toast({ tone: "error", title, body })
                },
              })
            }}
          />
        )}
      </Dialog>
    </div>
  )
}

/**
 * Why a delete failed, in words that lead somewhere.
 *
 * The backend has no clean refusal for an account that is still referenced â€” reviews it owns
 * or is assigned, comments, assignment history, engagement membership. The database rejects
 * the delete and the API answers 500 (a 409 would be the right answer; raised in the PR).
 * That is by far the likeliest 500 here, so it gets the specific explanation; anything the
 * shared banner can describe better (403, 404, network) still goes through it.
 */
function deleteFailure(err: unknown, email: string): { title: string; body: string } {
  if (err instanceof ApiError && (err.status === 409 || err.status >= 500)) {
    return {
      title: `Couldn't delete ${email}`,
      body: "The server couldn't delete this account. This usually happens when it has reviews, comments or assignments. Deactivate it instead.",
    }
  }
  return bannerFor(err, email, "save")
}

function LoadErrorBanner({
  error,
  resource,
}: {
  error: Error | null
  resource: string
}): JSX.Element | null {
  if (!error) return null
  const banner = bannerFor(error, resource)
  // An aborted request (navigation, a newer query) is not a failure worth showing.
  if (!banner.title) return null
  return (
    <div
      role="alert"
      className="rounded-md border border-people/30 bg-people-tint/40 px-4 py-3 text-body-sm text-people"
    >
      <div className="font-medium">{banner.title}</div>
      <div className="mt-0.5">{banner.body}</div>
    </div>
  )
}
